import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { Product, ProductDepotMetric, ProductPack } from 'depoto-core/src/entities'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { CoreService, NotificationService } from '../../../services'
import { SchemaUtil } from '../../../utils'
import * as Sentry from '@sentry/angular-ivy'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-modal-product-depot-metric',
  templateUrl: './modal-product-depot-metric.component.html',
  styleUrls: ['./modal-product-depot-metric.component.scss'],
})
export class ModalProductDepotMetricComponent implements OnInit {
  @ViewChild('modalDepotMetric') public childModal: ModalContainerComponent
  @Input() public product: Product
  @Input() public depot: any
  @Input() isOnProductsPage = false
  @Input() readonly = false
  @Input() productPack: ProductPack
  productDepotMetric: ProductDepotMetric
  isLoading = true
  submitted = false
  filters
  isCreating = false
  hasRoleMetricsUpdate: boolean
  hasRoleMetricsCreate: boolean

  isProductCountByPiece = false

  quantityMinimumForPack
  quantityOptimumForPack

  constructor(
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
  ) {}

  async ngOnInit() {
    this.isLoading = true
    this.hasRoleMetricsUpdate = this.core.services.user.hasRole('ROLE_PRODUCT_DEPOT_METRIC_UPDATE')
    this.hasRoleMetricsCreate = this.core.services.user.hasRole('ROLE_PRODUCT_DEPOT_METRIC_CREATE')
    await this.getDepotMetricList()
  }

  async getDepotMetricList() {
    let result
    this.filters = { product: this.product.id }
    if (this.depot?.id > 0) {
      this.filters = { product: this.product.id, depot: this.depot.id }
    }
    try {
      result = await this.core.services.productDepotMetric.getList(
        { page: 1, sort: 'id', direction: null, filters: this.filters },
        SchemaUtil.productDepotMetric,
      )
    } catch (err) {
      console.log(err)
      this.isLoading = false
      return
    }
    if (result?.items?.length > 0) {
      const productDepotMetric = result?.items?.find(object => object.depot?.id === this.depot.id)
      if (productDepotMetric?.id) {
        this.isCreating = false
        this.productDepotMetric = new ProductDepotMetric(productDepotMetric)
      } else {
        this.assignDefaultProductDepotMetric()
      }
    } else {
      this.assignDefaultProductDepotMetric()
    }
    this.isLoading = false
  }

  quantityMinimumChanged(quantity: number) {
    const pieces = Math.max(0, Math.round(Number(quantity) || 0))
    this.productDepotMetric.quantityMinimum = pieces
    if (this.isProductCountByPiece) {
      this.quantityMinimumForPack = Number((pieces / (this.productPack?.quantity || 1)).toFixed(2))
    }
  }

  quantityOptimumChanged(quantity: number) {
    const pieces = Math.max(0, Math.round(Number(quantity) || 0))
    this.productDepotMetric.quantityOptimum = pieces
    if (this.isProductCountByPiece) {
      this.quantityOptimumForPack = Number((pieces / (this.productPack?.quantity || 1)).toFixed(2))
    }
  }

  quantityMinimumForPackChanged(quantity: number) {
    this.quantityMinimumForPack = Math.max(0, Number(quantity) || 0)
    this.productDepotMetric.quantityMinimum = Math.round(
      Number(this.quantityMinimumForPack) * Number(this.productPack?.quantity || 1),
    )
  }

  quantityOptimumForPackChanged(quantity: number) {
    this.quantityOptimumForPack = Math.max(0, Number(quantity) || 0)
    this.productDepotMetric.quantityOptimum = Math.round(
      Number(this.quantityOptimumForPack) * Number(this.productPack?.quantity || 1),
    )
  }

  async getProductPack() {
    try {
      const result = await this.core.services.productPack.getList(
        { filters: { product: this.product.id } },
        SchemaUtil.productPack,
      )
      this.productPack = result.items[0]
    } catch (err) {
      console.log(err)
    }
  }

  assignDefaultProductDepotMetric() {
    this.isCreating = true
    this.productDepotMetric = new ProductDepotMetric({ product: this.product, depot: this.depot })
  }

  public async showChildModal() {
    if (this.product.tags.some(tag => tag.externalId === 'hs-count-by-piece')) {
      this.isProductCountByPiece = true
      this.setQuantitiesForPack()
    }
    this.childModal.showChildModal()
  }

  async setQuantitiesForPack() {
    if (!this.productPack) {
      await this.getProductPack()
    }
    const packQuantity = this.productPack?.quantity || 1
    this.quantityMinimumForPack =
      (this.productDepotMetric.quantityMinimum / packQuantity) % 1 != 0
        ? Number((this.productDepotMetric.quantityMinimum / packQuantity).toFixed(2))
        : this.productDepotMetric.quantityMinimum / packQuantity
    this.quantityOptimumForPack =
      (this.productDepotMetric.quantityOptimum / packQuantity) % 1 != 0
        ? Number((this.productDepotMetric.quantityOptimum / packQuantity).toFixed(2))
        : this.productDepotMetric.quantityOptimum / packQuantity
  }

  getPackPiecesIfInPack(): number {
    let byPieces = 1
    if (this.product?.tags?.some(t => t.externalId === 'hs-count-by-piece')) {
      byPieces = this.product?.packs?.find(t => t.externalId === 'hs-count-by-piece')?.quantity || 1
    }
    return byPieces
  }

  public hideChildModal(): void {
    this.childModal.hideChildModal()
  }

  async createProductDepotMetric() {
    this.submitted = true
    if (
      this.productDepotMetric.quantityMinimum < 0 ||
      this.productDepotMetric.quantityOptimum < 0 ||
      this.productDepotMetric.quantityMinimum % 1 != 0 ||
      this.productDepotMetric.quantityOptimum % 1 != 0
    ) {
      return
    }
    this.isLoading = true
    try {
      this.productDepotMetric.depot = this.depot
      this.productDepotMetric.product = this.product
      const result = await this.core.services.productDepotMetric.create(
        this.productDepotMetric,
        SchemaUtil.productDepotMetric,
      )
      this.notificationService.success(this.translateService.instant('alert.product.depot.metric.created'))
      await this.refreshData(result.id)
    } catch (err) {
      console.log(err)
    } finally {
      this.isLoading = false
      this.hideChildModal()
    }
  }

  async updateProductDepotMetric() {
    this.submitted = true
    if (
      this.productDepotMetric.quantityMinimum < 0 ||
      this.productDepotMetric.quantityOptimum < 0 ||
      this.productDepotMetric.quantityMinimum % 1 != 0 ||
      this.productDepotMetric.quantityOptimum % 1 != 0
    ) {
      return
    }
    this.isLoading = true
    try {
      const result = await this.core.services.productDepotMetric.update(
        this.productDepotMetric,
        SchemaUtil.productDepotMetric,
      )
      this.notificationService.success(this.translateService.instant('alert.product.depot.metric.updated'))
      await this.refreshData(result.id)
    } catch (err) {
      console.log(err)
    } finally {
      this.isLoading = false
      this.hideChildModal()
    }
  }

  async refreshData(id: number) {
    this.isLoading = true
    this.productDepotMetric = await this.core.services.productDepotMetric.getById(id, SchemaUtil.productDepotMetric)
    this.isLoading = false
  }

  async deleteProductDepotMetric() {
    const confirmation = confirm(this.translateService.instant('modal-product-depot-metric.delete.confirmation'))
    if (confirmation) {
      try {
        // dont delete whole metric. just null minimal and optimum quantities
        // await this.core.services.productDepotMetric.delete(this.productDepotMetric)
        this.productDepotMetric.quantityMinimum = null
        this.productDepotMetric.quantityOptimum = null
        const result = await this.core.services.productDepotMetric.update(
          this.productDepotMetric,
          SchemaUtil.productDepotMetric,
        )
        await this.refreshData(result.id)
        this.notificationService.success(this.translateService.instant('alert.product.depot.metric.deleted'))
        this.hideChildModal()
      } catch (err) {
        console.log(err)
      }
    }
  }
  protected readonly Number = Number
}
